import jsCookie from 'js-cookie'
import {
  Etat
} from '../services/abstracts/ServiceAbstract'
import Logger from '@nematis/js-snippets/assets/js/logging/debug-logger'

export default class CookieManager {
  constructor(parameters) {
    this.name = parameters.name || 'rousquille'
    this.environment = parameters.environment
    this.cookieDomain = parameters.cookieDomain
    this.paramGetCookieName = parameters.paramGetCookieName || '_rsq'
    this.privacyUrl = parameters.privacyUrl
    this.etat = this.getEtat()
    this.parameters = parameters
    this.timeExpire = 365
    this.services = []

    if (this.parameters.forceExpire) {
      // The number of day can't be higher than 1 year
      this.timeExpire = Math.min(this.parameters.forceExpire, this.timeExpire)
    }
  }

  get status() {
    return this.etat
  }

  set status(etat) {
    if (this.etat == etat) {
      return
    }

    this.etat = etat
    this.create(etat, `${this.name}Root`)
  }

  /**
   * Enable Global CookieManager status
   */
  enable() {
    this.status = 'answered'
  }

  /**
   * Read a cookie
   * @param {string|boolean} name
   * @param defaultValue
   */
  read(name = false, defaultValue = undefined) {
    if (!name) {
      name = this.name
      defaultValue = {}
    }

    let content = defaultValue
    try {
      switch (this.environment) {
        case 'production':
          content = jsCookie.get(name)
          break
        default:
          // retrieve all cookies and then try to get current cookie value from this object during development
          // because we might have older Rousquille versions still storing cookies on main domain
          // and JS Cookie will return that old cookie if it exists, while we want to get subdomain cookie
          const allCookies = jsCookie.get()
          content = allCookies[name] || defaultValue
          break
      }

      content = JSON.parse(content)

      // development only: keep services effectively enabled in current config (don't get services from other sites)
      if (name === this.name && typeof content === 'object' && this.environment !== 'production') {
        const servicesIds = this.services.map((service) => service.identifier)
        const enabledServices = {}
        for (const key of Object.keys(content)) {
          if (servicesIds.includes(key)) {
            enabledServices[key] = content[key]
          }
        }

        content = enabledServices
      }
    } catch (e) {
    }

    return content
  }

  /**
   * Set cookie status of a service
   * @param {Service} service
   */
  setService(service) {
    const cookie = this.read() || {}

    cookie[service.identifier] = service.getStatus()

    this.create(cookie)
  }

  /**
   * Get cookie status of a service
   * @param {service} service
   */
  getServiceStatus(service) {
    const cookie = this.read() || {}
    if (!cookie[service.identifier]) {
      return false
    }

    return cookie[service.identifier] || false
  }

  /**
   * Create/Update a cookie
   * @param {string|json} value
   * @param {string} name
   */
  create(value, name = false) {
    if (!name) {
      name = this.name
    }

    if (typeof value === 'object') {
      value = JSON.stringify(value)
    }

    jsCookie.set(name, value, {
      expires: this.timeExpire,
      domain: this.cookieDomain
    })
  }

  /**
   * Remove a cookie
   * @param {string} name
   */
  remove(name) {
    jsCookie.remove(name)
  }

  /**
   * Purge All Cookie in arr
   * @param {Array} arr
   */
  purge(arr) {
    for (let i = 0; i < arr.length; i += 1) {
      document.cookie = `${arr[i]}=; expires=Thu, 01 Jan 2000 00:00:00 GMT; path=/;`
      document.cookie = `${arr[i]}=; expires=Thu, 01 Jan 2000 00:00:00 GMT; path=/; domain=.${location.hostname};`
      document.cookie = `${arr[i]}=; expires=Thu, 01 Jan 2000 00:00:00 GMT; path=/; domain=.${location.hostname.split('.').slice(-2).join('.')};`
    }
  }

  /**
   * Attempt to purge all cookies from service
   * Note: cookies shared with another enabled service won't be purged
   *
   * @param {service} serviceToPurge
   */
  purgeService(serviceToPurge) {
    const cookiesToPurge = serviceToPurge.cookies
    if (!cookiesToPurge.length) {
      return
    }

    // make sure that the cookies we're about to purge aren't used by another enabled service
    this.services.forEach((service) => {
      if (service.identifier !== serviceToPurge.identifier && service.getStatus() === Etat.enabled) {
        // reversed loop to make sure index is still valid even if we remove some data while iterating
        for (let i = cookiesToPurge.length - 1; i >= 0; i--) {
          // this cookie is shared with another service: don't purge it
          if (service.cookies.indexOf(cookiesToPurge[i]) !== -1) {
            cookiesToPurge.splice(i, 1)
          }
        }
      }
    })

    // purge remaining cookies (if any)
    if (cookiesToPurge.length) {
      this.purge(cookiesToPurge)
    }
  }

  setCookiesFromGet() {
    // @todo this.paramGetCookieName
  }

  /**
   * Get CookieManager status (not dynamique!)
   */
  getEtat() {
    return this.read(`${this.name}Root`, Etat.wait)
  }

  getDynEtat() {
    // on verifie si des services sont encore à [wait]
    const cookies = this.read() || {}
    const servicesWait = []

    for (const cookie in cookies) {
      if (cookies[cookie] == Etat.wait) {
        servicesWait.push(cookie)
      }
    }

    // tous les services sont paramétrés
    return !servicesWait.length ? Etat.enabled : Etat.wait
  }

  /**
   * Initialize cookie service
   * @param {service} services
   */
  init(services) {
    // on essaie de récupérer un get pour recréer le cookie
    this.setCookiesFromGet()

    // store services for further use
    this.services = services

    for (const service of services) {
      if (this.getServiceStatus(service) === false) {
        this.setService(service)
      }
    }

    // Poursuivre la navigation autorise le depot de cookie (edit 30/03/2021 : plus maintenant)
    this.runAutoConsent()
  }

  /**
   * Poursuivre la navigation autorise le depot de cookie
   * - a la 2eme page visitee
   * - si on n'est pas sur la page de politique de conf
   * - on allow tous les services en wait (on garde les autres choix de l'utilisateur)
   *
   * Edit 30/03/2021 : la poursuite de la nav ne doit plus autoriser
   */
  runAutoConsent() {
    Logger.log('RUN auto consent')
    // on verifie si des services sont encore à [wait]
    const cookies = this.read()
    const servicesWait = []
    const cookieInitPath = this.read(`${this.name}Init`)

    for (const cookie in cookies) {
      if (cookies[cookie] == Etat.wait) {
        servicesWait.push(cookie)
      }
    }

    // tous les services sont paramétrés
    if (!servicesWait.length) {
      this.enable()
      // on vide la page d'attérissage
      this.purge([`${this.name}Init`])
      return
    }

    this.purge([`${this.name}Root`])

    // il manque des services à paramétrer
    if (!cookieInitPath) {
      this.create(window.location.pathname, `${this.name}Init`)
    }
    /* edit 30/03/2021 : poursuivre la navigation ne doit plus faire office de validation
    else if (window.location.pathname != this.privacyUrl && window.location.href != this.privacyUrl) {
      // on a poursuivi la navigation donc on autorise les cookies en attente
      for (const indice in servicesWait) {
        this.setService({
          identifier:servicesWait[indice],
          status: Etat.enabled
        })
      }
      // on vide la page d'attérissage
      this.purge([`${this.name}Init`])
    }
    */
  }
}
